import { HttpClient, HttpHeaders, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { QuoteSessionQuery } from '@app/store/session/session.query';
import { RESOURCES, API_VERSIONS, getEndpoint, getVersionedEndpoint } from '@constants/api-endpoints';
import { AddressValidationRequest } from '@models/AddressValidationRequest';
import { AddressValidationResponse } from '@models/AddressValidationResponse';
import { QuoteAnalytics } from '@models/Analytics';
import { Contact } from '@models/Contact';
import { Pet } from '@models/Pet';
import { PurchasePolicyResponse } from '@models/PurchasePolicyResponse';
import { QuoteSession } from '@models/QuoteSession';
import { RetrieveQuoteRequest } from '@models/RetrieveQuoteRequest';
import { StripePurchasePolicyRequest } from '@models/StripePurchasePolicyRequest';
import { SubProducerResponse } from '@models/SubProducerResponse';
import { TestAssociation } from '@models/TestAssociation';
import { VetClinic } from '@models/VetClinic';
import { UtmService } from '@services/utm.service';
import { Observable } from 'rxjs';

@Injectable({
	providedIn: 'root',
})
export class OrchestratorService {
	public get quoteId(): string {
		return this.sessionQuery.getQuoteId();
	}

	constructor(
		private readonly http: HttpClient,
		private readonly sessionQuery: QuoteSessionQuery,
		private readonly utmService: UtmService
	) { }

	/**
	 *
	 * @param subProducer the sub producer number matched with the `code` key
	 */
	public getOrgCodeFromSubProducer(
		subProducer: string,
	): Observable<SubProducerResponse> {
		return this.http.get<SubProducerResponse>(
			`${getEndpoint(RESOURCES.BRAND)}?subProducerNumber=${subProducer}`,
		);
	}

	/**
	 * Used to retrieve an exisiting quote
	 *
	 * @param req The retrieve quote request
	 */
	public retrieveQuote(req: RetrieveQuoteRequest): Observable<QuoteSession> {
		return this.http.post<QuoteSession>(
			getEndpoint(RESOURCES.RETRIEVE_QUOTE),
			req,
		);
	}

	/**
	 * Used to start a new quote
	 *
	 * @param req The form data from pet-information.component
	 */
	public generateNewQuote(
		req: Partial<QuoteSession>,
	): Observable<QuoteSession> {
		return this.http.post<QuoteSession>(getEndpoint(RESOURCES.QUOTE), req);
	}

	/**
	 * Used to add a pet to the policy.
	 *
	 * The newly added pet is set as the active pet TODO:
	 *
	 * @param pet The form data from add-edit-pet.component
	 */
	public addNewPet(pet: Pet): Observable<QuoteSession> {
		const quoteId = this.sessionQuery.getQuoteId();

		return this.http.post<QuoteSession>(
			getEndpoint(RESOURCES.QUOTE, quoteId, RESOURCES.PET),
			pet,
		);
	}

	/**
	 * Used to update a pet's information
	 *
	 * After the pet is updated it is set as the active pet
	 *
	 * @param updatePetRequest The form data from add-edit-pet.component
	 */
	public updatePet(updatePetRequest: Pet): Observable<QuoteSession> {
		const quoteId = this.sessionQuery.getQuoteId();
		const url = getEndpoint(
			RESOURCES.QUOTE,
			quoteId,
			RESOURCES.PET,
			updatePetRequest.petId ?? '',
		);

		return this.http.put<QuoteSession>(url, updatePetRequest);
	}

	/**
	 * Used to update a pet's selected coverage
	 *
	 * After the pet is updated it is set as the active pet
	 *
	 * @param coverageId Id related to coverage details (deductible, copay, annual max, wellness)
	 * @param updatePetRequest The form data from add-edit-pet.component
	 */
	public updatePetCoverage(
		quoteId: string,
		coverageId: string,
		updatePetRequest: Pet,
	): Observable<QuoteSession> {
		const url = getEndpoint(
			RESOURCES.QUOTE,
			quoteId,
			RESOURCES.PET,
			updatePetRequest.petId ?? '',
			RESOURCES.COVERAGE,
			coverageId,
		);

		return this.http.put<QuoteSession>(url, updatePetRequest);
	}

	/**
	 * Used to update contact information
	 *
	 * @param updateContactRequest Form data from contact-info.component
	 */
	public updateContact(
		updateContactRequest: Contact,
	): Observable<QuoteSession> {
		const quoteId = this.sessionQuery.getQuoteId();

		return this.http.put<QuoteSession>(
			getEndpoint(RESOURCES.QUOTE, quoteId, RESOURCES.CONTACT),
			updateContactRequest,
		);
	}

	/**
	 * Used to delete a single pet from a policy
	 *
	 * @param quoteSessionId The GUID of the current quote session
	 * @param petId The GUID of the pet that should be marked for removal
	 */
	public removePet(petId: string): Observable<QuoteSession> {
		const response = this.http.delete<QuoteSession>(
			getEndpoint(RESOURCES.QUOTE, this.quoteId, RESOURCES.PET, petId),
		);

		return response;
	}

	public stripePurchasePolicy(req: StripePurchasePolicyRequest): Observable<PurchasePolicyResponse> {
		const quoteId = this.sessionQuery.getQuoteId();

		return this.http.post<PurchasePolicyResponse>(
			getVersionedEndpoint(API_VERSIONS.API_VERSION_TWO, RESOURCES.QUOTE, quoteId, RESOURCES.PURCHASE),
			req
		);
	}

	/**
	 * Gets the vet clinics within 30 miles of the zip code provided
	 *
	 * @param zipCode the zip code to search for vet clinics (within 30 miles)
	 */
	public getVetClinics(zipCode: string): Observable<VetClinic[]> {
		return this.http.get<VetClinic[]>(
			`${getEndpoint(RESOURCES.VET_CLINIC)}?zipCode=${zipCode}`,
		);
	}

	/**
	 * Saves the primary vet from the congratulations page
	 *
	 * @param vetClinicAccountId the account ID of the selected vetClinic
	 */
	public saveVetClinic(
		vetClinicAccountId: string,
	): Observable<HttpResponse<void>> {
		const contactId: string = this.sessionQuery.contactIdValue() ?? ``;

		return this.http.post<void>(
			getEndpoint(RESOURCES.VET_CLINIC),
			{ contactId, vetClinicAccountId },
			{ observe: 'response' },
		);
	}

	/**
	 * Saves the quote when the user goes to ROUTES.CONTACT_INFO
	 *
	 * @param quoteId the id of the quote to be saved
	 */
	public saveQuote(quoteId: string): Observable<QuoteSession> {
		return this.http.patch<QuoteSession>(
			getEndpoint(RESOURCES.QUOTE, quoteId, RESOURCES.SAVE),
			null,
		);
	}

	public checkout(): Observable<QuoteSession> {
		const quoteId = this.sessionQuery.getQuoteId();
		const analyticsInfo = { ...this.utmService.getUtmCookie() };

		return this.http.post<QuoteSession>(
			getEndpoint(RESOURCES.QUOTE, quoteId, RESOURCES.CHECKOUT),
			analyticsInfo,
		);
	}

	public getState(): Observable<string> {
		const headers = new HttpHeaders();
		headers.set('responseType', 'text');

		return this.http.get(getEndpoint(RESOURCES.STATE), {
			headers,
			responseType: 'text',
		});
	}

	public getShouldRedirect(memberId: string): Observable<boolean> {
		return this.http.get<boolean>(
			`${getEndpoint(
				RESOURCES.SHOULD_MEMBER_NUMBER_REDIRECT,
			)}?memberId=${memberId}`,
		);
	}

	public validateAddresses(
		request: AddressValidationRequest,
	): Observable<AddressValidationResponse> {
		return this.http.post<AddressValidationResponse>(
			`${getEndpoint(RESOURCES.VALIDATION)}/list`,
			request,
		);
	}

	public saveTestDetails(
		request: TestAssociation,
	): Observable<TestAssociation> {
		return this.http.post<TestAssociation>(
			`${getEndpoint()}/TestAssociation`,
			request,
		);
	}

	public setLeadClick(request: QuoteAnalytics): Observable<string> {
		return this.http.post<string>(
			`${getEndpoint()}/Analytics/leadclick`,
			request,
			{ responseType: 'text' as 'json' },
		);
	}
}
